var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { KompisElement, property } from '@inter-ikea-kompis/web-component';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { ModalHeader as SkapaModalHeader, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import BuyInStoreModalEnum from '../../enums/BuyInStoreModalEnum.js';
import BuyInStoreCard from '../buy-in-store-card/BuyInStoreCard.js';
/**
 * Opens a sheet with a QR code for the user to scan to complete their purchase of a Kiosk design.
 *
 * @event modalClose Modal close.
 * @event modalOpen Modal open.
 */
export default class BuyInStore extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // A list of store availability objects.
        this.storeAvailabilities = null;
        // Visible modal. Use ZipInModalEnum.
        this.visibleModal = null;
        // Where the sheet will open. The values are flipped in RTL, meaning "right" will open to the left and vice versa.
        this.sheetAlignment = SheetAlignmentEnum.right;
        // Link used in the QR code to take the user to the summary page on their phone. This should link directly to the picking list of the summary page.
        this.buyInStoreLink = null;
        this.sheetElement = null;
        this.cardElement = null;
    }
    render() {
        return null;
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('kompis-buy-in-store-card', BuyInStoreCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === BuyInStoreModalEnum.buyInStore) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        if (this.cardElement) {
            this.setCardProperties(this.cardElement);
        }
        return true;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        if (!translations) {
            throw new Error('Translations must be defined either through the "translations" or "localizedInformation" prop.');
        }
        return translations;
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
            this.cardElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        this.requestUpdate();
        const sheetElement = this.createSheetElement();
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        this.sheetElement = sheetElement;
        this.cardElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-buy-in-store-card'));
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: BuyInStoreModalEnum.buyInStore
            }
        }));
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const card = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(card);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.setAttribute('aria-label', this.getTranslations().buyInStoreHeader);
        sheetElement.size = 'small';
        sheetElement.style.zIndex = '500';
        sheetElement.fullHeight = true;
        sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const card = this.createInternalElement('kompis-buy-in-store-card');
        this.setCardProperties(card);
        return card;
    }
    setCardProperties(cardElement) {
        cardElement.localizedInformation = this.localizedInformation;
        cardElement.translations = this.translations;
        cardElement.storeAvailabilities = this.storeAvailabilities;
        cardElement.buyInStoreLink = this.buyInStoreLink;
    }
}
__decorate([
    property({ type: Object, required: true })
], BuyInStore.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], BuyInStore.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], BuyInStore.prototype, "translations", void 0);
__decorate([
    property({ type: Array })
], BuyInStore.prototype, "storeAvailabilities", void 0);
__decorate([
    property({ type: String })
], BuyInStore.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], BuyInStore.prototype, "sheetAlignment", void 0);
__decorate([
    property({ type: String })
], BuyInStore.prototype, "buyInStoreLink", void 0);
//# sourceMappingURL=BuyInStore.js.map